import { Box, Divider, Flex, Text } from "@chakra-ui/react";
import { InputGroup } from "v2/components/funnel/forms/InputGroup";
import { ControlledAirlineAutocomplete } from "./ControlledAirlineAutocomplete";
import { ControlledInput } from "./ControlledInput";
import { useTranslation } from "react-i18next";
import { Control, useFieldArray, useWatch } from "react-hook-form";
import { Airline, ItineraryFlight } from "../types/intake-funnel";
import { ControlledDatepicker } from "./ControlledDatepicker";
import { addDays } from "date-fns";
import { SUPPORTED_FUTURE_DAYS } from "../../settings/constants";
import { AirplaneIcon } from "v2/components/funnel/ui-elements/airplane-icon/Airplane";

export function sanitizeFlightNumber(flightNumber: string) {
  const flightNumberMatch = flightNumber.match(/\d{1,4}[a-z]?/);
  return flightNumberMatch ? flightNumberMatch[0] : "";
}

export function getAirlineCode(airline: Airline | null) {
  if (!airline) return "";
  const isEasyjetAirline = airline?.uniqueIdentifier?.toLowerCase().includes("easyjet");
  return isEasyjetAirline || !airline.iata ? airline.icao : airline.iata;
}

export function ItineraryDetailsControl({ control }: ItineraryDetailsControlProps) {
  const { t } = useTranslation();
  const { fields: itineraryFlights } = useFieldArray({
    name: "itineraryFlights",
    control,
  });

  const watchedFlights = useWatch({
    control,
    name: "itineraryFlights",
  });

  const minDate = watchedFlights?.[0]?.flightDate;

  return (
    <Box>
      {itineraryFlights.map((itineraryFlight, index: number) => (
        <Box
          key={itineraryFlight.id}
          mt={index === 0 ? 0 : 6}
          data-testid="itineraryDetailsControl"
        >
          {index !== 0 && (
            <Divider
              orientation="horizontal"
              w={{ base: "100%", md: "75%" }}
              color="greyscale.400"
              opacity={1}
            />
          )}
          <Flex alignItems="center" mb={4} mt={6}>
            <Text fontSize="md">{`${itineraryFlight.departureAirport?.city} (${itineraryFlight.departureAirport?.iata})`}</Text>
            <AirplaneIcon color="greyscale.500" mx={2} />
            <Text fontSize="md">{`${itineraryFlight.arrivalAirport?.city} (${itineraryFlight.arrivalAirport?.iata})`}</Text>
          </Flex>
          <InputGroup>
            <ControlledAirlineAutocomplete
              control={control}
              dataTestId="airlineAutocomplete"
              name={`itineraryFlights.${index}.airline`}
              label={t("steps.itinerary_details.airline")}
              placeholder={t("steps.itinerary_details.airline_placeholder")}
            />
            <Flex mt="8px" width="100%" align="flex-start" data-sentry-unmask>
              <Box mr="6">
                <ControlledInput
                  isDouble
                  control={control}
                  data-testid="flightNumber"
                  leftAddonText={getAirlineCode(watchedFlights[index]?.airline) || ""}
                  name={`itineraryFlights.${index}.flightNumber`}
                  placeholder={"1234"}
                  label={t("steps.itinerary_details.flight_number")}
                  sx={{ w: "100%" }}
                  maxLength={5}
                  minWidth="78px"
                  sanitizeInput={sanitizeFlightNumber}
                />
              </Box>
              <Box width="100%">
                <ControlledDatepicker
                  control={control}
                  name={`itineraryFlights.${index}.flightDate`}
                  maxDate={addDays(new Date(), SUPPORTED_FUTURE_DAYS)}
                  placeholder={t("steps.ec261.personal_details.date_of_birth.placeholder")}
                  label={t("steps.itinerary_details.date")}
                  minDate={minDate && index > 0 ? minDate : undefined}
                />
              </Box>
            </Flex>
          </InputGroup>
        </Box>
      ))}
    </Box>
  );
}

type ItineraryDetailsControlProps = {
  control: Control<{
    itineraryFlights: ItineraryFlight[];
  }>;
};
